import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, FileText, Clock, AlertTriangle, Search, X, FolderLock,
} from 'lucide-react';
import {
  API, ISSUE_TYPES, STATUS_OPTIONS, headers, StatusBadge, IssueLabel, recommendedAction,
} from './shared';

// ──────────────────────────────────────────────
// CASE LIST
// ──────────────────────────────────────────────
function CaseList({ onSelect, onCreateNew }) {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [issueFilter, setIssueFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [q, setQ] = useState('');

  // Debounce keyword input
  useEffect(() => {
    const t = setTimeout(() => setQ(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (statusFilter) params.set('status', statusFilter);
        if (issueFilter) params.set('issue_type', issueFilter);
        if (q) params.set('q', q);
        const url = `${API}/api/case-locker/cases${params.toString() ? `?${params}` : ''}`;
        const res = await fetch(url, { headers: headers(false) });
        const data = await res.json();
        setCases(data.cases || []);
      } catch { setCases([]); }
      setLoading(false);
    })();
  }, [statusFilter, issueFilter, q]);

  const hasAnyFilter = !!(statusFilter || issueFilter || q);

  return (
    <div className="cl-list" data-testid="case-list">
      <div className="cl-list-header">
        <div>
          <h2 className="cl-list-title">Your Cases</h2>
          <p className="cl-list-sub">{cases.length} case{cases.length !== 1 ? 's' : ''}</p>
        </div>
        <button className="cl-btn-primary" data-testid="create-case-btn" onClick={onCreateNew}>
          <Plus size={16} /> New Case
        </button>
      </div>

      {/* Keyword search */}
      <div className="cl-search-row">
        <div className="cl-search-wrap">
          <Search size={14} />
          <input
            className="cl-search-input"
            data-testid="case-search-input"
            placeholder="Search title, summary, timeline…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          {searchInput && (
            <button
              type="button"
              className="cl-search-clear"
              data-testid="case-search-clear"
              onClick={() => setSearchInput('')}
              aria-label="Clear search"
            >
              <X size={12} />
            </button>
          )}
        </div>
        <select
          className="cl-select cl-issue-filter"
          data-testid="case-issue-filter"
          value={issueFilter}
          onChange={(e) => setIssueFilter(e.target.value)}
        >
          <option value="">All issue types</option>
          {ISSUE_TYPES.map(t => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
        {hasAnyFilter && (
          <button
            type="button"
            className="cl-btn-sm"
            data-testid="clear-filters-btn"
            onClick={() => { setStatusFilter(''); setIssueFilter(''); setSearchInput(''); }}
          >
            Clear
          </button>
        )}
      </div>

      <div className="cl-filters">
        {[{ value: '', label: 'All' }, ...STATUS_OPTIONS].map(s => (
          <button key={s.value} className={`cl-filter-btn ${statusFilter === s.value ? 'active' : ''}`}
            onClick={() => setStatusFilter(s.value)}>
            {s.label}
          </button>
        ))}
      </div>

      {loading ? <div className="cl-loading">Loading cases...</div> : (
        cases.length === 0 ? (
          <div className="cl-empty" data-testid="no-cases">
            <FolderLock size={40} />
            <p>{hasAnyFilter ? 'No cases match those filters.' : 'No cases yet. Create your first case to start tracking.'}</p>
          </div>
        ) : (
          <div className="cl-case-cards">
            {cases.map(c => {
              // Compute days-since for a deadline-style chip
              const lastActivity = c.last_activity_at ? new Date(c.last_activity_at) : null;
              const daysSince = lastActivity ? Math.floor((Date.now() - lastActivity.getTime()) / 86400000) : null;
              const evidence = (c.attachments?.length || 0) + (c.timeline?.length || 0);
              const recommended = recommendedAction(c);
              const isStale = daysSince !== null && daysSince > 14;
              return (
                <button
                  key={c.case_id}
                  className={`cl-case-card cl-case-card--modern ${isStale ? 'cl-case-card--stale' : ''}`}
                  data-testid={`case-card-${c.case_id}`}
                  onClick={() => onSelect(c.case_id)}
                >
                  <div className="cl-card-top">
                    <span className="cl-card-id">{c.case_id}</span>
                    <StatusBadge status={c.status} />
                  </div>
                  <h3 className="cl-card-title">{c.title}</h3>
                  <div className="cl-card-meta">
                    <IssueLabel type={c.issue_type} />
                    {c.incident_date && <span>{c.incident_date}</span>}
                  </div>
                  {c.summary && <p className="cl-card-summary">{c.summary.slice(0, 120)}{c.summary.length > 120 ? '...' : ''}</p>}

                  {/* P85: Modern dashboard row — deadline + evidence + recommended action */}
                  <div className="cl-card-stats">
                    <div className="cl-stat">
                      <span className="cl-stat-label">Last Update</span>
                      <span className={`cl-stat-value ${isStale ? 'cl-stat-value--warn' : ''}`}>
                        {daysSince === null ? '—' : daysSince === 0 ? 'Today' : `${daysSince}d ago`}
                      </span>
                    </div>
                    <div className="cl-stat">
                      <span className="cl-stat-label">Evidence</span>
                      <span className="cl-stat-value">
                        {evidence} {evidence === 1 ? 'item' : 'items'}
                      </span>
                    </div>
                    <div className="cl-stat cl-stat--action">
                      <span className="cl-stat-label">Recommended</span>
                      <span className="cl-stat-value cl-stat-value--action">{recommended}</span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )
      )}
    </div>
  );
}

// ──────────────────────────────────────────────
// CREATE CASE FORM
// ──────────────────────────────────────────────
function CreateCaseForm({ onCreated, onCancel }) {
  const [form, setForm] = useState({
    title: '', issue_type: 'other', incident_date: '', summary: '',
    people_involved: [], witnesses: [], notes: ''
  });
  const [personName, setPersonName] = useState('');
  const [personRole, setPersonRole] = useState('');
  const [witnessName, setWitnessName] = useState('');
  const [saving, setSaving] = useState(false);

  function addPerson() {
    if (!personName.trim()) return;
    setForm(f => ({ ...f, people_involved: [...f.people_involved, { name: personName.trim(), role: personRole.trim(), notes: '' }] }));
    setPersonName(''); setPersonRole('');
  }

  function addWitness() {
    if (!witnessName.trim()) return;
    setForm(f => ({ ...f, witnesses: [...f.witnesses, witnessName.trim()] }));
    setWitnessName('');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/case-locker/cases`, {
        method: 'POST', headers: headers(), body: JSON.stringify(form)
      });
      const data = await res.json();
      if (data.ok) onCreated(data.case.case_id);
    } catch (e) { console.warn('create case failed', e); }
    setSaving(false);
  }

  return (
    <form className="cl-form" onSubmit={handleSubmit} data-testid="create-case-form">
      <h2 className="cl-form-title">Create New Case</h2>

      <label className="cl-label">Title *
        <input className="cl-input" data-testid="case-title-input" value={form.title}
          onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. Overtime bypass December 15" required />
      </label>

      <div className="cl-form-row">
        <label className="cl-label">Issue Type
          <select className="cl-select" data-testid="case-issue-select" value={form.issue_type}
            onChange={e => setForm(f => ({ ...f, issue_type: e.target.value }))}>
            {ISSUE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </label>
        <label className="cl-label">Incident Date
          <input className="cl-input" type="date" data-testid="case-date-input" value={form.incident_date}
            onChange={e => setForm(f => ({ ...f, incident_date: e.target.value }))} />
        </label>
      </div>

      <label className="cl-label">Summary
        <textarea className="cl-textarea" data-testid="case-summary-input" value={form.summary}
          onChange={e => setForm(f => ({ ...f, summary: e.target.value }))} rows={3}
          placeholder="What happened? Describe the incident..." />
      </label>

      <div className="cl-label">People Involved
        <div className="cl-inline-add">
          <input className="cl-input" placeholder="Name" value={personName} onChange={e => setPersonName(e.target.value)} />
          <input className="cl-input" placeholder="Role" value={personRole} onChange={e => setPersonRole(e.target.value)} />
          <button type="button" className="cl-btn-sm" onClick={addPerson}><Plus size={14} /></button>
        </div>
        {form.people_involved.map((p, i) => (
          <span key={i} className="cl-tag">{p.name} {p.role ? `(${p.role})` : ''}
            <button type="button" onClick={() => setForm(f => ({ ...f, people_involved: f.people_involved.filter((_, j) => j !== i) }))}><X size={12} /></button>
          </span>
        ))}
      </div>

      <div className="cl-label">Witnesses
        <div className="cl-inline-add">
          <input className="cl-input" placeholder="Witness name" value={witnessName} onChange={e => setWitnessName(e.target.value)} />
          <button type="button" className="cl-btn-sm" onClick={addWitness}><Plus size={14} /></button>
        </div>
        {form.witnesses.map((w, i) => (
          <span key={i} className="cl-tag">{w}
            <button type="button" onClick={() => setForm(f => ({ ...f, witnesses: f.witnesses.filter((_, j) => j !== i) }))}><X size={12} /></button>
          </span>
        ))}
      </div>

      <label className="cl-label">Notes
        <textarea className="cl-textarea" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2}
          placeholder="Additional notes or context..." />
      </label>

      <div className="cl-form-actions">
        <button type="button" className="cl-btn-secondary" onClick={onCancel}>Cancel</button>
        <button type="submit" className="cl-btn-primary" data-testid="save-case-btn" disabled={saving || !form.title.trim()}>
          {saving ? 'Creating...' : 'Create Case'}
        </button>
      </div>
    </form>
  );
}

export { CaseList, CreateCaseForm };
